// Filter popover for DataTableShell — "Where [column] [operator] [value]" condition rows,
// "+ Add Condition", and Clear all / Apply. Conditions are edited as a draft and only take
// effect on Apply, matching the design's footer buttons.
import React, {useState} from 'react';
import {Box, ButtonBase, ClickAwayListener, Popper, Stack, Typography} from '@mui/material';
import {tokens} from '../../../themes/ThemeCustomization';

import paginationDropdownIcon from '../../../assets/shell/pagination-dropdown-icon.svg';

export const FILTER_OPERATORS = [
    {id: 'contains', label: 'contains', test: (value, needle) => value.includes(needle)},
    {id: 'not_contains', label: 'does not contain', test: (value, needle) => !value.includes(needle)},
    {id: 'equals', label: 'is', test: (value, needle) => value === needle},
    {id: 'starts_with', label: 'starts with', test: (value, needle) => value.startsWith(needle)},
    {id: 'ends_with', label: 'ends with', test: (value, needle) => value.endsWith(needle)}
];

// Conditions with an empty value are ignored, so a half-filled row never hides everything.
export const matchesConditions = (conditions, getValue) =>
    conditions.every((condition) => {
        const needle = condition.value.trim().toLowerCase();
        if (!needle) return true;
        const operator = FILTER_OPERATORS.find((op) => op.id === condition.operator) || FILTER_OPERATORS[0];
        return operator.test(String(getValue(condition.field) ?? '').toLowerCase(), needle);
    });

const controlSx = {
    height: 36,
    border: `1px solid ${tokens.color.neutral200}`,
    borderRadius: '6px',
    backgroundColor: tokens.color.white,
    fontSize: '14px',
    fontFamily: 'inherit',
    color: tokens.color.neutral950
};

const SelectBox = ({value, onChange, options, width}) => (
    <Box sx={{position: 'relative', width, flexShrink: 0}}>
        <Box
            component="select"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{...controlSx, width: '100%', pl: 1.5, pr: 4, appearance: 'none', cursor: 'pointer', outline: 'none'}}
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </Box>
        <Box
            component="img"
            src={paginationDropdownIcon}
            alt=""
            sx={{position: 'absolute', right: 12, top: 11, width: 14, height: 14, pointerEvents: 'none'}}
        />
    </Box>
);

const TrashIcon = ({disabled}) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={disabled ? tokens.color.neutral300 : tokens.color.neutral600} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.6 9h6.8L12 4M6.5 6.5v4M9.5 6.5v4" />
    </svg>
);

const TableFilterPanel = ({anchorEl, open, onClose, columns, conditions, onApply}) => {
    const newCondition = () => ({field: columns[0]?.field, operator: 'contains', value: ''});
    const [draft, setDraft] = useState(() => (conditions.length ? conditions : [newCondition()]));

    const update = (index, patch) => setDraft((prev) => prev.map((c, i) => (i === index ? {...c, ...patch} : c)));
    const remove = (index) => setDraft((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [newCondition()]));

    const columnOptions = columns.map((col) => ({value: col.field, label: col.headerName}));
    const operatorOptions = FILTER_OPERATORS.map((op) => ({value: op.id, label: op.label}));

    return (
        <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{zIndex: 1300}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
            <ClickAwayListener onClickAway={(event) => !anchorEl?.contains(event.target) && onClose()}>
                <Box
                    sx={{
                        width: 'min(620px, calc(100vw - 32px))',
                        backgroundColor: tokens.color.white,
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: '8px',
                        boxShadow: tokens.shadow.toast,
                        overflow: 'hidden'
                    }}
                >
                    <Stack spacing={1.5} sx={{p: 2}}>
                        {draft.map((condition, index) => (
                            <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
                                <Typography variant="body2" fontWeight={500} sx={{width: 48, flexShrink: 0, color: tokens.color.neutral950}}>
                                    {index === 0 ? 'Where' : 'And'}
                                </Typography>
                                <SelectBox width={150} value={condition.field} onChange={(field) => update(index, {field})} options={columnOptions} />
                                <SelectBox width={150} value={condition.operator} onChange={(operator) => update(index, {operator})} options={operatorOptions} />
                                <Box
                                    component="input"
                                    value={condition.value}
                                    onChange={(e) => update(index, {value: e.target.value})}
                                    placeholder="Enter value"
                                    sx={{...controlSx, flex: 1, minWidth: 0, px: 1.5, outline: 'none', '&:focus': {borderColor: tokens.color.sky600}}}
                                />
                                <ButtonBase aria-label="Remove condition" onClick={() => remove(index)} sx={{width: 28, height: 28, borderRadius: '6px'}}>
                                    <TrashIcon disabled={draft.length === 1 && !condition.value} />
                                </ButtonBase>
                            </Stack>
                        ))}
                        <ButtonBase
                            onClick={() => setDraft((prev) => [...prev, newCondition()])}
                            sx={{alignSelf: 'flex-start', gap: 0.75, color: tokens.color.sky600}}
                        >
                            <Typography component="span" sx={{fontSize: '18px', lineHeight: 1}}>
                                +
                            </Typography>
                            <Typography variant="body2" fontWeight={500} sx={{color: 'inherit'}}>
                                Add Condition
                            </Typography>
                        </ButtonBase>
                    </Stack>

                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1.5}
                        sx={{backgroundColor: tokens.color.neutral50, borderTop: `1px solid ${tokens.color.neutral200}`, px: 2, py: 1.5}}
                    >
                        <ButtonBase
                            onClick={() => {
                                setDraft([newCondition()]);
                                onApply([]);
                            }}
                            sx={{height: 32, px: 1.5, borderRadius: '6px', border: `1px solid ${tokens.color.neutral200}`, backgroundColor: tokens.color.white}}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                Clear all
                            </Typography>
                        </ButtonBase>
                        <ButtonBase
                            onClick={() => onApply(draft.filter((c) => c.value.trim()))}
                            sx={{height: 32, px: 2, borderRadius: '6px', backgroundColor: tokens.color.sky600, color: tokens.color.white}}
                        >
                            <Typography variant="body2" fontWeight={500} sx={{color: 'inherit'}}>
                                Apply
                            </Typography>
                        </ButtonBase>
                    </Stack>
                </Box>
            </ClickAwayListener>
        </Popper>
    );
};

export default TableFilterPanel;
